// ESignEmailConfig.js
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useQuill } from 'react-quilljs';
import 'quill/dist/quill.snow.css';
import EmailPreviewModal from '../../../components/PrintPreview/EmailPreviewModal';
import { fetchEmailTemplateSettings, updateEmailTemplateSettings } from '../../../slice/HomeSlice';
import {
  EmailSMSConfigContent,
  EmailSMSConfigContentHeader,
  EmailSMSConfigContentBody,
  EmailSMSConfigForm,
  EmailSMSConfigLabel,
  EmailSMSConfigUpdateButton,
  EmailSMSPreviewEmailButton,
  FieldWrapper,
  EditorWrapper,
  QuillContainer,
  StyledEditor,
  SubjectInputContainer,
  SubjectInsertTagsButton,
  SubjectTagsDropdown,
  SubjectTagOption,
  SubjectEditableDiv 
} from './EmailSmsStyled';
import Quill from 'quill';
import { chipsToPlaceholders, placeholdersToChips } from '../../../utils/emailTemplateTags';

// ---- Configure Quill Size ----
const Size = Quill.import('formats/size');
Size.whitelist = ['small', false, 'large', 'huge'];
Quill.register(Size, true);

// ---- Custom Tag Blot ----
const Embed = Quill.import('blots/embed');
class TagBlot extends Embed {
  static blotName = 'tag';
  static tagName = 'span';
  static className = 'custom-tag';

  static create(value) {
    const node = super.create(value);
    node.dataset.tag = value;
    node.setAttribute('contenteditable', false);

    const textSpan = document.createElement('span');
    textSpan.textContent = value;
    node.appendChild(textSpan);

    const closeButton = document.createElement('span');
    closeButton.className = 'tag-close-button';
    node.appendChild(closeButton);

    return node;
  }

  static value(node) {
    return node.dataset.tag;
  }
}
Quill.register(TagBlot, true);

const createTagNode = (value) => {
  const node = document.createElement(TagBlot.tagName);
  node.className = TagBlot.className;
  node.dataset.tag = value;
  node.setAttribute('contenteditable', false);

  const textSpan = document.createElement('span');
  textSpan.textContent = value;
  node.appendChild(textSpan);

  const closeButton = document.createElement('span');
  closeButton.className = 'tag-close-button';
  node.appendChild(closeButton);

  return node;
};

const createSubjectTagHtml = (value) => createTagNode(value).outerHTML;

// ---- Custom Toolbar ----
const CustomToolbar = () => (
  <div id="custom-quill-toolbar">
    <select className="ql-size" defaultValue="">
      <option value="">Normal</option>
      <option value="small">Small</option>
      <option value="large">Large</option>
      <option value="huge">Huge</option>
    </select>
    <button className="ql-bold" />
    <button className="ql-italic" />
    <button className="ql-underline" />
    <button className="ql-link" />
    <button className="ql-list" value="ordered" />
    <button className="ql-list" value="bullet" />
    <button className="ql-clean" />
  </div>
);

// ---- Component ----
const ESignEmailConfig = ({ setIsLoading, handleToaster, activeLabel, isLoading, activeType }) => {
  const dispatch = useDispatch();
  const { emailTemplate } = useSelector((state) => state.home);

  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [showTags, setShowTags] = useState(false);
  const [showSubjectTags, setShowSubjectTags] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [tagOptions, setTagOptions] = useState(['Client Name', 'Recipient', 'File Name', 'Todays Date 01 June, 2025']);
  const [subjectCursorPosition, setSubjectCursorPosition] = useState(null);
  const [isFetching, setIsFetching] = useState(false); // NEW: initial fetch flag

  const subjectInputRef = useRef();
  const subjectTagsDropdownRef = useRef();
  const bodyTagsDropdownRef = useRef();

  const { quill, quillRef } = useQuill({
    placeholder: 'Type your email here...',
    modules: { toolbar: '#custom-quill-toolbar' },
    formats: ['tag', 'size', 'bold', 'italic', 'underline', 'link', 'list', 'clean'],
  });

  // ---- Fetch template only on mount / activeLabel change ----
  useEffect(() => {
    if (!activeType.API_TYPE) return;

    setIsFetching(true);

    dispatch(fetchEmailTemplateSettings(activeType.API_TYPE))
      .unwrap()
      .finally(() => setIsFetching(false));
  }, [activeType.API_TYPE, dispatch]);

  // ---- Reset editor on quill init ----
  useEffect(() => {
    if (quill) quill.setText('');
  }, [quill]);

  // ---- Populate editor + subject only on initial fetch ----
  useEffect(() => {
    if (!emailTemplate || !quill || !isFetching) return;

    const htmlBody = emailTemplate?.data?.body || '';
    const convertedBody = placeholdersToChips(htmlBody, createTagNode);

    const subject = emailTemplate?.data?.subject || '';

    // Step 1: Convert all spaces to &nbsp;
    let withNbsp = subject.replaceAll(' ', '&nbsp;');
    
    // Step 2: Convert {tags} to HTML tags
    const convertedSubject = withNbsp.replaceAll(
      /\{([^{}]*)\}/g,
      (match, tag) => {
        if (!tag) return match;
        return createSubjectTagHtml(tag.trim());
      }
    );

    setEmailBody(htmlBody);
    setEmailSubject(subject);
    setTagOptions(emailTemplate?.data?.available_placeholders || []);

    quill.root.innerHTML = convertedBody;
    if (subjectInputRef.current) subjectInputRef.current.innerHTML = convertedSubject;
  }, [emailTemplate, quill, isFetching]);

  // ---- Subject change handler ----
  const handleSubjectChange = () => {
    const input = subjectInputRef.current;
    if (!input) return;

    let htmlContent = input.innerHTML;
    let processedContent = htmlContent.replaceAll(
      /<span class="custom-tag"[^>]*data-tag="([^"]*)"[^>]*>(?:(?!<\/span>).)*<\/span>/g,
      (match, tag) => `{${tag}}`
    );
    processedContent = processedContent.replaceAll('<br>', '').trim();
    // Use a more specific pattern to avoid ReDoS - match complete HTML tags
    setEmailSubject(processedContent.replaceAll(/<\/?[a-zA-Z][^>]*>/g, '').trim());
  };

  // ---- Subject input listeners ----
  useEffect(() => {
    const input = subjectInputRef.current;
    if (!input) return;

    input.addEventListener('input', handleSubjectChange);

    const handleCloseClick = (e) => {
      if (e.target.classList.contains('tag-close-button')) {
        const tagNode = e.target.closest('.custom-tag');
        if (tagNode) {
          tagNode.remove();
          handleSubjectChange();
          e.preventDefault();
          e.stopPropagation();
        }
      } else storeSubjectCursorPosition();
    };

    const handleKeyUp = () => storeSubjectCursorPosition();

    input.addEventListener('click', handleCloseClick);
    input.addEventListener('keyup', handleKeyUp);

    return () => {
      input.removeEventListener('input', handleSubjectChange);
      input.removeEventListener('click', handleCloseClick);
      input.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // ---- Quill editor listeners ----
  useEffect(() => {
    if (!quill) return;

    const handleTextChange = () => {
      setEmailBody(chipsToPlaceholders(quill.root.innerHTML));
    };

    const handleCloseClick = (e) => {
      if (e.target.classList.contains('tag-close-button')) {
        const blotNode = e.target.closest('.custom-tag');
        Quill.find(blotNode)?.remove();
        e.preventDefault();
        e.stopPropagation();
      }
    };

    quill.on('text-change', handleTextChange);
    quill.root.addEventListener('click', handleCloseClick);

    return () => {
      quill.off('text-change', handleTextChange);
      quill.root.removeEventListener('click', handleCloseClick);
    };
  }, [quill]);

  // ---- Cursor helpers ----
  const storeSubjectCursorPosition = () => {
    const input = subjectInputRef.current;
    if (!input) return;
    const sel = globalThis.getSelection();
    if (sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      const container = r.commonAncestorContainer;
      if (input.contains(container) || container === input) {
        setSubjectCursorPosition({
          startContainer: r.startContainer,
          startOffset: r.startOffset,
          endContainer: r.endContainer,
          endOffset: r.endOffset,
        });
      }
    }
  };

  const insertTagInSubject = (tag) => {
    const input = subjectInputRef.current;
    if (!input) return;

    input.focus();
    const sel = globalThis.getSelection();
    let range;

    if (subjectCursorPosition) {
      try {
        range = document.createRange();
        range.setStart(subjectCursorPosition.startContainer, subjectCursorPosition.startOffset);
        range.setEnd(subjectCursorPosition.endContainer, subjectCursorPosition.endOffset);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      } catch {}
    }

    if (!range && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (input.contains(r.commonAncestorContainer)) range = r;
    }

    if (!range) {
      range = document.createRange();
      if (input.childNodes.length) range.setStartAfter(input.lastChild);
      else range.setStart(input, 0);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }

    const tagHtml = createSubjectTagHtml(tag);
    const frag = range.createContextualFragment(tagHtml);
    const insertedNode = frag.firstChild;

    range.deleteContents();
    range.insertNode(frag);

    if (insertedNode) {
      const newRange = document.createRange();
      newRange.setStartAfter(insertedNode);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    }

    setSubjectCursorPosition(null);
    handleSubjectChange();
  };

  // ---- Outside click handlers for dropdowns ----
  useEffect(() => {
    if (!showSubjectTags) return;
    const handleClickOutside = (e) => {
      if (
        subjectTagsDropdownRef.current &&
        !subjectTagsDropdownRef.current.contains(e.target) &&
        !subjectInputRef.current.contains(e.target)
      ) setShowSubjectTags(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSubjectTags]);

  useEffect(() => {
    if (!showTags) return;
    const handleClickOutside = (e) => {
      if (bodyTagsDropdownRef.current && !bodyTagsDropdownRef.current.contains(e.target)) {
        setShowTags(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTags]);

  // ---- Insert tag into Quill editor ----
  const insertTagAtCursor = (tag) => {
    if (!quill) return;
    quill.focus();
    const range = quill.getSelection(true);
    if (!range) return;
    quill.insertEmbed(range.index, 'tag', tag, Quill.sources.USER);
    quill.setSelection(range.index + 1);
  };

  // ---- Update button ----
  const handleUpdate = async () => {
    setIsLoading(true);
    try {
   
      const cleanedSubject = emailSubject
  ? emailSubject.replaceAll('&nbsp;', ' ').trimEnd()
  : '';

    
      await dispatch(updateEmailTemplateSettings({
        type: activeType.API_TYPE,
        data: { subject: cleanedSubject, body: emailBody, is_active: true },
      })).unwrap();
      handleToaster('Template updated successfully!', true);
    } catch (err) {
      console.log('err ====', err)
      handleToaster(err?.response?.data?.message || 'Failed to update template');
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Render ----
  return (
    <EmailSMSConfigContent>
      <EmailSMSConfigContentHeader>
        {activeLabel}
        <div style={{ display: 'flex', gap: 12 }}>
          <EmailSMSPreviewEmailButton onClick={() => setShowPreviewModal(true)}>
            Preview Email
          </EmailSMSPreviewEmailButton>
          <EmailSMSConfigUpdateButton onClick={handleUpdate}>Update</EmailSMSConfigUpdateButton>
        </div>
      </EmailSMSConfigContentHeader>

      <EmailSMSConfigContentBody>
        <EmailSMSConfigForm>
          <FieldWrapper>
            <EmailSMSConfigLabel>Email Subject</EmailSMSConfigLabel>
            <br />
            <SubjectInputContainer>
              <SubjectEditableDiv
                contentEditable="true"
                ref={subjectInputRef}
                placeholder="Enter email subject"
              />
              <SubjectInsertTagsButton
                type="button"
                onClick={() => {
                  storeSubjectCursorPosition();
                  setShowSubjectTags(!showSubjectTags);
                }}
              >
                + Insert tags
              </SubjectInsertTagsButton>
              {showSubjectTags && (
                <SubjectTagsDropdown ref={subjectTagsDropdownRef}>
                  {tagOptions.map((tag) => (
                    <SubjectTagOption key={tag} onClick={() => { insertTagInSubject(tag); setShowSubjectTags(false); }}>
                      {tag}
                    </SubjectTagOption>
                  ))}
                </SubjectTagsDropdown>
              )}
            </SubjectInputContainer>
          </FieldWrapper>

          <FieldWrapper>
            <EmailSMSConfigLabel>Email Body</EmailSMSConfigLabel>
            <EditorWrapper>
              <QuillContainer>
                <StyledEditor>
                  <div className="editor-header">
                    <CustomToolbar />
                    <div style={{ position: 'relative' }}>
                      <button className="insert-tags-button" type="button" onClick={() => setShowTags(!showTags)}>
                        + Insert Tags
                      </button>
                      {showTags && (
                        <div className="insert-tags-dropdown" ref={bodyTagsDropdownRef}>
                          {tagOptions.map((tag) => (
                            <button
                              key={tag}
                              className="insert-tag-option"
                              type="button"
                              onClick={() => { insertTagAtCursor(tag); setShowTags(false); }}
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div ref={quillRef} />
                </StyledEditor>
              </QuillContainer>
            </EditorWrapper>
          </FieldWrapper>
        </EmailSMSConfigForm>
      </EmailSMSConfigContentBody>

      <EmailPreviewModal
        activeLabel={activeLabel}
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        emailBody={emailBody}
      />
    </EmailSMSConfigContent>
  );
};

ESignEmailConfig.propTypes = {
  setIsLoading: PropTypes.func,
  handleToaster: PropTypes.func,
  activeLabel: PropTypes.string,
  isLoading: PropTypes.bool,
  activeType: PropTypes.shape({
    API_TYPE: PropTypes.string.isRequired,
  }).isRequired,
};

export default ESignEmailConfig;
